import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiError, customerApi } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function CustomerAuthPage() {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(f.email, f.password);
        toast.success("Welcome back!");
        navigate("/account");
      } else if (mode === "register") {
        await register({ name: f.name, email: f.email, password: f.password, phone: f.phone });
        toast.success("Account created — check your inbox to verify your email");
        navigate("/account");
      } else {
        await customerApi.post("/customer/forgot-password", { email: f.email, base_url: window.location.origin });
        toast.success("If that email has an account, a reset link is on its way");
        setMode("login");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-md mx-auto px-4 py-14">
        <div className="rounded-2xl border bg-white p-7 shadow-sm">
          <h1 className="text-2xl font-extrabold" data-testid="auth-heading">
            {mode === "login" ? "Sign in to ACP Online" : mode === "register" ? "Create your account" : "Reset your password"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === "login" ? "Access your orders, quotes and saved details." :
             mode === "register" ? "Track orders and check out faster next time." :
             "Enter your email and we'll send you a reset link."}
          </p>

          {mode !== "forgot" && (
            <>
              <Button type="button" data-testid="google-signin-btn" onClick={googleSignIn} variant="outline"
                className="w-full mt-5 h-11 font-semibold gap-2">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
                Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-4 text-xs text-muted-foreground">
                <span className="flex-1 h-px bg-slate-200" /> or {mode === "login" ? "sign in" : "sign up"} with email <span className="flex-1 h-px bg-slate-200" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <div><Label>Full name</Label><Input data-testid="auth-name" value={f.name} onChange={set("name")} required /></div>
                <div><Label>Phone (optional)</Label><Input data-testid="auth-phone" value={f.phone} onChange={set("phone")} /></div>
              </>
            )}
            <div><Label>Email</Label><Input data-testid="auth-email" type="email" value={f.email} onChange={set("email")} required /></div>
            {mode !== "forgot" && (
              <div><Label>Password</Label><Input data-testid="auth-password" type="password" value={f.password} onChange={set("password")} required /></div>
            )}
            {mode === "login" && (
              <button type="button" onClick={() => setMode("forgot")} className="text-xs text-accent hover:underline" data-testid="forgot-link">
                Forgot password?
              </button>
            )}
            <Button type="submit" data-testid="auth-submit" disabled={busy}
              className="w-full h-11 bg-steel text-white hover:bg-steel-light font-semibold">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
            </Button>
          </form>

          <div className="mt-5 text-sm text-center text-muted-foreground">
            {mode === "login" ? (
              <>New to ACP? <button data-testid="switch-register" onClick={() => setMode("register")} className="text-accent font-semibold hover:underline">Create an account</button></>
            ) : (
              <>Already have an account? <button data-testid="switch-login" onClick={() => setMode("login")} className="text-accent font-semibold hover:underline">Sign in</button></>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4">
          Prefer to check out as a guest? <Link to="/" className="text-accent hover:underline">Keep shopping</Link> — an account isn't required.
        </p>
      </main>
      <Footer />
    </div>
  );
}
